import * as React from "react"
import { Card } from "@/components/ui/card"
import { formatCurrency } from "@/lib/utils"

interface CategoryBalance {
  userId: string
  userName: string
  amount: number // positive means they're owed, negative means they owe
}

interface CategorySummaryCardProps {
  categoryName: string
  categoryEmoji: string
  balances: CategoryBalance[]
  currencyCode?: string
}

export const CategorySummaryCard: React.FC<CategorySummaryCardProps> = ({
  categoryName,
  categoryEmoji,
  balances,
  currencyCode = 'EUR'
}) => {
  // Generate initials from name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Generate color based on name (consistent colors for same names)
  const getColorForName = (name: string) => {
    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-700' },
      { bg: 'bg-purple-100', text: 'text-purple-700' },
      { bg: 'bg-green-100', text: 'text-green-700' },
      { bg: 'bg-orange-100', text: 'text-orange-700' },
      { bg: 'bg-pink-100', text: 'text-pink-700' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      { bg: 'bg-red-100', text: 'text-red-700' },
      { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    ]

    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
  }

  // Get background color for category emoji
  const getCategoryBgColor = (emoji?: string) => {
    if (!emoji) return 'bg-gray-50'

    // Map common emoji categories to colors
    const emojiColors: { [key: string]: string } = {
      '🍕': 'bg-orange-50',
      '🍔': 'bg-orange-50',
      '🍜': 'bg-orange-50',
      '🍱': 'bg-orange-50',
      '🏠': 'bg-blue-50',
      '🚗': 'bg-green-50',
      '✈️': 'bg-sky-50',
      '🎬': 'bg-purple-50',
      '🛒': 'bg-amber-50',
      '💊': 'bg-red-50',
      '🎮': 'bg-indigo-50',
    }

    return emojiColors[emoji] || 'bg-gray-50'
  }

  // Sort balances: positive amounts (owed money) first, then negative (owe money)
  const sortedBalances = [...balances].sort((a, b) => b.amount - a.amount)

  return (
    <Card className="relative bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow p-4">
      {/* Combined header with category and participants */}
      <div className="flex items-center gap-3">
        {/* Category section */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${getCategoryBgColor(categoryEmoji)} flex items-center justify-center`}>
            <span className="text-2xl">{categoryEmoji}</span>
          </div>
          <div>
            <p className="font-medium text-gray-900">{categoryName}</p>
          </div>
        </div>

        {/* Vertical divider */}
        <div className="h-10 w-px bg-gray-200"></div>

        {/* Participants section */}
        <div className="flex items-center gap-3 overflow-x-auto flex-1">
          {sortedBalances.map((balance, index) => {
            const userColors = getColorForName(balance.userName)
            const isPositive = balance.amount > 0

            return (
              <React.Fragment key={balance.userId}>
                {index > 0 && <span className="text-gray-300">•</span>}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div className={`w-6 h-6 rounded-full ${userColors.bg} flex items-center justify-center`}>
                    <span className={`text-xs font-semibold ${userColors.text}`}>
                      {getInitials(balance.userName)}
                    </span>
                  </div>
                  <div className="flex flex-col ml-1">
                    <span className="text-sm text-gray-700">{balance.userName}</span>
                    <span className={`text-sm font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive ? '+' : ''}{formatCurrency(Math.abs(balance.amount), currencyCode)}
                    </span>
                  </div>
                </div>
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </Card>
  )
}
